import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, QrCode, Upload, Smartphone, Bell } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";
import { PillSwitch } from "@/components/PillSwitch";

export default function Settings() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">Settings</h1>
        <p className="text-sm text-slate-400 mt-1">Configure your games, condition pricing and payment options.</p>
      </div>

      <GamesCard />
      <MultipliersCard />
      <BoxedAlertsCard />
      <PaymentsCard />
    </div>
  );
}

function BoxedAlertsCard() {
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState("");
  const [lastSent, setLastSent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/boxed/notify").then(({ data }) => {
      setEnabled(!!data.enabled); setEmail(data.email || ""); setLastSent(data.last_sent || null);
    }).catch(() => {});
  }, []);

  const save = async (en, em) => {
    setSaving(true);
    try {
      const { data } = await api.post("/boxed/notify", { enabled: en, email: em });
      setEnabled(!!data.enabled); setEmail(data.email || "");
      toast.success(data.enabled ? "Boxed sell alerts are on" : "Boxed sell alerts turned off");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/boxed/notify/test");
      toast.success(`Test alert sent to ${data.email} (${data.count} card${data.count === 1 ? "" : "s"})`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  return (
    <div className="rounded-2xl border p-6 mt-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }} data-testid="settings-boxed-alerts-card">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-blue-950/80 border border-blue-800/60 flex items-center justify-center"><Bell className="h-5 w-5 text-blue-400" /></div>
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-100">Boxed sell alerts</h2>
            <p className="text-xs text-slate-500 max-w-md">Email me after each Boxed sync when my in-stock cards match the buylist — only when the sellable list changes. Shows exactly what to send and the total payout value.</p>
          </div>
        </div>
        <PillSwitch checked={enabled} color="emerald" testid="boxed-alert-toggle" onChange={(v) => save(v, email)} />
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@store.com" data-testid="boxed-alert-email"
          className="flex-1 min-w-[220px] px-3 py-2.5 rounded-xl border bg-transparent text-sm text-slate-100 outline-none focus:ring-2 focus:ring-blue-500/40" style={{ borderColor: "var(--pk-border)" }} />
        <button onClick={() => save(enabled, email)} disabled={saving} data-testid="boxed-alert-save"
          className="px-5 py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-60">
          {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={sendTest} disabled={testing || !email} data-testid="boxed-alert-test"
          className="px-5 py-2.5 rounded-xl border text-sm text-slate-200 hover:bg-[var(--pk-hover)] disabled:opacity-50" style={{ borderColor: "var(--pk-border)" }}>
          {testing ? "Sending…" : "Send test"}
        </button>
      </div>
      {lastSent && <p className="text-[11px] text-slate-600 mt-3">Last alert sent {new Date(lastSent).toLocaleString()}</p>}
    </div>
  );
}

const PAY_APPS = [
  { key: "zelle", label: "Zelle" },
  { key: "venmo", label: "Venmo" },
  { key: "paypal", label: "PayPal" },
  { key: "cashapp", label: "Cash App" },
];

function PaymentsCard() {
  const [qrs, setQrs] = useState({ zelle: "", venmo: "", paypal: "", cashapp: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const refs = useRef({});

  useEffect(() => {
    api.get("/settings").then(({ data }) => setQrs({ zelle: "", venmo: "", paypal: "", cashapp: "", ...(data.payment_qrs || {}) })).catch(() => {});
  }, []);

  const upload = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setQrs((q) => ({ ...q, [key]: data.url }));
      toast.success("Image uploaded — click Save to apply");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(""); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/settings/payments", qrs);
      toast.success("Payment QR codes saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const src = (u) => (u?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  return (
    <div className="rounded-2xl border p-6 mt-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }} data-testid="settings-payments-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-xl bg-cyan-950/80 border border-cyan-800/60 flex items-center justify-center"><Smartphone className="h-5 w-5 text-cyan-400" /></div>
        <div>
          <h2 className="font-heading text-lg font-bold text-slate-100">Payment QR codes</h2>
          <p className="text-xs text-slate-500">Upload your Zelle, Venmo, PayPal & Cash App QR images — shown to buyers at electronic checkout.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {PAY_APPS.map((a) => (
          <div key={a.key} className="rounded-xl border p-3 text-center" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
            <div className="text-sm font-semibold text-slate-200 mb-2 flex items-center justify-center gap-1.5">{a.label}{qrs[a.key] && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}</div>
            <div className="aspect-square rounded-lg bg-white/90 flex items-center justify-center overflow-hidden mb-2">
              {qrs[a.key] ? <img src={src(qrs[a.key])} alt={a.label} data-testid={`payment-qr-preview-${a.key}`} className="w-full h-full object-contain" /> : <QrCode className="h-8 w-8 text-slate-400" />}
            </div>
            <input ref={(el) => (refs.current[a.key] = el)} type="file" accept="image/*" hidden data-testid={`payment-qr-upload-${a.key}`}
              onChange={(e) => upload(a.key, e.target.files?.[0])} />
            <button onClick={() => refs.current[a.key]?.click()} disabled={uploading === a.key} data-testid={`payment-qr-upload-btn-${a.key}`}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg border text-xs text-slate-300 hover:bg-[var(--pk-hover)] disabled:opacity-50" style={{ borderColor: "var(--pk-border)" }}>
              {uploading === a.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {qrs[a.key] ? "Replace" : "Upload"}
            </button>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving} data-testid="settings-payments-save"
        className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save Payment Codes
      </button>
    </div>
  );
}

function MultipliersCard() {
  const [data, setData] = useState(null);
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/multipliers").then(({ data }) => {
      setData(data);
      const v = {};
      data.games.forEach((g) => {
        v[g.id] = {};
        data.conditions.forEach((c) => {
          const cur = data.current?.[g.id]?.[c];
          v[g.id][c] = Math.round(((cur != null ? cur : data.defaults[c]) || 0) * 100);
        });
      });
      setVals(v);
    }).catch(() => {});
  }, []);

  const set = (g, c, val) => setVals((p) => ({ ...p, [g]: { ...p[g], [c]: val } }));

  const reset = (g) => setVals((p) => ({ ...p, [g]: Object.fromEntries(data.conditions.map((c) => [c, Math.round((data.defaults[c] || 0) * 100)])) }));

  const save = async () => {
    setSaving(true);
    try {
      const multipliers = {};
      Object.entries(vals).forEach(([g, conds]) => {
        multipliers[g] = {};
        Object.entries(conds).forEach(([c, pct]) => { multipliers[g][c] = (parseFloat(pct) || 0) / 100; });
      });
      await api.post("/settings/multipliers", { multipliers });
      toast.success("Condition pricing saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!data) return null;

  return (
    <div className="rounded-2xl border p-6 mt-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }} data-testid="settings-multipliers-card">
      <h2 className="font-heading text-lg font-bold text-slate-100 mb-1">Condition pricing (% of market)</h2>
      <p className="text-xs text-slate-500 mb-4">Auto-prices Lightly→Damaged cards off market when no live listing data exists. NM is always 100%.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-500 text-left">
              <th className="py-2 pr-4">Game</th>
              {data.conditions.map((c) => <th key={c} className="py-2 px-2">{c}</th>)}
              <th className="py-2 px-2" />
            </tr>
          </thead>
          <tbody>
            {data.games.map((g) => (
              <tr key={g.id} data-testid={`mult-row-${g.id}`} className="border-t" style={{ borderColor: "var(--pk-border)" }}>
                <td className="py-2 pr-4 text-slate-200 whitespace-nowrap">{g.label}</td>
                {data.conditions.map((c) => (
                  <td key={c} className="py-2 px-2">
                    <div className="flex items-center gap-1">
                      <input type="number" min="0" max="100" value={vals[g.id]?.[c] ?? ""} data-testid={`mult-${g.id}-${c}`}
                        onChange={(e) => set(g.id, c, e.target.value)}
                        className="w-16 px-2 py-1 rounded-lg border bg-transparent text-slate-100 outline-none text-right" style={{ borderColor: "var(--pk-border)" }} />
                      <span className="text-slate-500 text-xs">%</span>
                    </div>
                  </td>
                ))}
                <td className="py-2 px-2">
                  <button onClick={() => reset(g.id)} data-testid={`mult-reset-${g.id}`}
                    className="text-xs text-slate-400 hover:text-amber-400 flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> Reset</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={save} disabled={saving} data-testid="settings-multipliers-save"
        className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save Condition Pricing
      </button>
    </div>
  );
}

function GamesCard() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [saving, setSaving] = useState(false);
  const { refreshGames } = useSubscription();

  useEffect(() => {
    api.get("/settings/games").then(({ data }) => { setData(data); setSel(data.enabled || []); }).catch(() => {});
  }, []);

  const toggle = (id) => {
    setSel((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (data.max_games != null && cur.length >= data.max_games) {
        toast.error(`Your plan allows ${data.max_games} game series. Upgrade to enable more.`);
        return cur;
      }
      return [...cur, id];
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.post("/settings/games", { games: sel });
      toast.success(`Saved ${res.enabled.length} game series${res.syncing?.length ? " · syncing catalog…" : ""}`);
      refreshGames();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!data) return null;
  const limit = data.max_games == null ? "Unlimited" : `${sel.length}/${data.max_games}`;

  return (
    <div className="rounded-2xl border p-6 mt-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }} data-testid="settings-games-card">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-heading text-lg font-bold text-slate-100">Game series (TCGs)</h2>
        <span className="text-xs font-semibold text-amber-400">{limit}</span>
      </div>
      <p className="text-xs text-slate-500 mb-4">Pick which games appear in your inventory searches. Your plan limits how many you can enable.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {data.available.map((g) => {
          const on = sel.includes(g.id);
          return (
            <button key={g.id} type="button" onClick={() => toggle(g.id)} data-testid={`game-toggle-${g.id}`}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm text-left transition-colors ${on ? "border-amber-500/50 bg-amber-500/10 text-amber-300" : "border-[var(--pk-border)] text-slate-400 hover:bg-[var(--pk-hover)]"}`}>
              <span className={`h-3.5 w-3.5 rounded-full shrink-0 ${on ? "bg-amber-400" : "bg-slate-600"}`} />
              <span className="truncate">{g.label}</span>
            </button>
          );
        })}
      </div>
      <button onClick={save} disabled={saving} data-testid="settings-games-save"
        className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save Game Selection
      </button>
    </div>
  );
}
